import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'

import type {
  PlatformAssistantInstance,
  PlatformAssistantResponse,
} from '@aeris/contracts'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  Section,
  Select,
  notify,
} from '@aeris/ui'

import { fetchMessagingServers } from '../lib/api/platform.ts'
import {
  ApiError,
  fetchPlatformAssistant,
  fetchPlatformAssistantInstances,
  linkPlatformAssistant,
  platformAssistantPairing,
  savePlatformAssistant,
  unlinkPlatformAssistant,
} from '../lib/api.ts'

/**
 * O assistente do Aeris, na Plataforma (F256, ADR-0045).
 *
 * Um número só, do Aeris, para todas as contas: os clientes perguntam por
 * ele os números da própria loja e recebem o resumo do dia. Aqui o dono da
 * Plataforma escolhe a instância (de um servidor já cadastrado, que não seja
 * de loja nenhuma), pareia pelo QR e grava a chave da OpenAI do Aeris, o
 * modelo e o teto padrão de gasto por conta.
 */

const STATUS_LABELS: Readonly<
  Record<PlatformAssistantResponse['status'], string>
> = {
  unlinked: 'Sem número',
  pairing: 'Esperando o QR',
  active: 'No ar',
}

const USE_LABELS: Readonly<Record<PlatformAssistantInstance['use'], string>> = {
  aeris: 'é o número do Aeris',
  store: 'de uma loja',
  elsewhere: 'manda para outro sistema',
  none: 'livre',
}

const message = (failure: unknown): string =>
  failure instanceof ApiError
    ? failure.message
    : 'Não foi possível concluir. Tente de novo.'

const NumberPicker = ({
  current,
}: {
  readonly current: PlatformAssistantResponse
}): ReactNode => {
  const queryClient = useQueryClient()
  const servers = useQuery({
    queryKey: ['platform-messaging-servers'],
    queryFn: async ({ signal }) => await fetchMessagingServers(signal),
  })
  const [serverId, setServerId] = useState(current.serverId ?? '')
  const [instanceId, setInstanceId] = useState('')
  const [confirmReplace, setConfirmReplace] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const instances = useQuery({
    queryKey: ['platform-assistant-instances', serverId],
    queryFn: async () => await fetchPlatformAssistantInstances(serverId),
    enabled: serverId !== '',
  })
  const chosen = instances.data?.find((instance) => instance.id === instanceId)

  const link = useMutation({
    meta: { errorShownInline: true },
    mutationFn: async () =>
      await linkPlatformAssistant({
        serverId,
        instanceId,
        replaceWebhook: confirmReplace,
        version: current.version,
      }),
    onSuccess: (saved) => {
      setError(null)
      queryClient.setQueryData(['platform-assistant'], saved)
      notify.success('Número do Aeris ligado. Agora pareie pelo QR.')
    },
    onError: (failure: unknown) => {
      setError(message(failure))
    },
  })

  return (
    <div className="flex flex-col gap-3">
      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Servidor">
          {(field) => (
            <Select
              {...field}
              value={serverId}
              onChange={(event) => {
                setServerId(event.target.value)
                setInstanceId('')
              }}
            >
              <option value="">Escolha</option>
              {(servers.data ?? []).map((server) => (
                <option key={server.id} value={server.id}>
                  {server.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Instância">
          {(field) => (
            <Select
              {...field}
              value={instanceId}
              disabled={serverId === '' || instances.isPending}
              onChange={(event) => {
                setInstanceId(event.target.value)
                setConfirmReplace(false)
              }}
            >
              <option value="">Escolha</option>
              {(instances.data ?? []).map((instance) => (
                <option
                  key={instance.id}
                  value={instance.id}
                  disabled={instance.use === 'store'}
                >
                  {instance.name}
                  {instance.phone === null ? '' : ` — ${instance.phone}`} (
                  {instance.use === 'store'
                    ? `da loja ${instance.storeName ?? ''}`
                    : USE_LABELS[instance.use]}
                  )
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {chosen?.use === 'elsewhere' ? (
        <label className="flex items-center gap-2 text-(length:--text-sm)">
          <input
            type="checkbox"
            checked={confirmReplace}
            onChange={(event) => {
              setConfirmReplace(event.target.checked)
            }}
          />
          Hoje ela manda as mensagens para{' '}
          {chosen.webhookHost ?? 'outro sistema'}. Ligar como o número do Aeris
          desliga esse sistema dela.
        </label>
      ) : null}
      <div>
        <Button
          size="sm"
          loading={link.isPending}
          disabled={
            instanceId === '' ||
            (chosen?.use === 'elsewhere' && !confirmReplace)
          }
          onClick={() => {
            link.mutate()
          }}
        >
          Ligar como o número do Aeris
        </Button>
      </div>
    </div>
  )
}

const Pairing = (): ReactNode => {
  const queryClient = useQueryClient()
  const [started, setStarted] = useState(false)
  const state = useQuery({
    queryKey: ['platform-assistant-pairing', started],
    queryFn: async () => await platformAssistantPairing(false),
    enabled: started,
    // O QR gira a cada ~20 s.
    refetchInterval: (query) =>
      query.state.data?.state === 'paired' ? false : 5_000,
  })
  const start = useMutation({
    meta: { errorShownInline: true },
    mutationFn: async () => await platformAssistantPairing(true),
    onSuccess: () => {
      setStarted(true)
    },
  })
  const paired = state.data?.state === 'paired'
  useEffect(() => {
    if (paired) {
      void queryClient.invalidateQueries({ queryKey: ['platform-assistant'] })
    }
  }, [paired, queryClient])

  const data = state.data ?? start.data
  return (
    <div className="flex flex-col gap-3">
      {start.isError ? (
        <InlineAlert tone="danger">{message(start.error)}</InlineAlert>
      ) : null}
      {data?.state === 'qr' && data.qrCode !== null ? (
        <div className="flex flex-col items-start gap-2">
          <img
            src={data.qrCode}
            alt="QR para parear o número do Aeris"
            className="h-56 w-56 rounded-(--radius-md) border border-(--border-default) bg-white p-2"
          />
          <p className="text-(length:--text-xs) text-(--text-muted)">
            No celular do número do Aeris: WhatsApp → Aparelhos conectados →
            Conectar um aparelho.
          </p>
        </div>
      ) : data?.state === 'waiting' ? (
        <p className="text-(length:--text-sm) text-(--text-muted)">
          Esperando o WhatsApp mandar o QR…
        </p>
      ) : data?.state === 'paired' ? (
        <p className="text-(length:--text-sm)">
          Pareado{data.linkedPhone === null ? '' : ` com ${data.linkedPhone}`}.
        </p>
      ) : null}
      {paired ? null : (
        <div>
          <Button
            size="sm"
            variant="secondary"
            loading={start.isPending}
            onClick={() => {
              start.mutate()
            }}
          >
            Mostrar o QR
          </Button>
        </div>
      )}
    </div>
  )
}

const KeyForm = ({
  current,
}: {
  readonly current: PlatformAssistantResponse
}): ReactNode => {
  const queryClient = useQueryClient()
  const [llmKey, setLlmKey] = useState('')
  const [model, setModel] = useState(current.llmModel ?? '')
  const [cap, setCap] = useState(current.defaultMonthlyCapUsd)
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    meta: { errorShownInline: true },
    mutationFn: async () =>
      await savePlatformAssistant({
        // Vazio: mantém a chave gravada (ela nunca volta na tela).
        ...(llmKey.trim() === '' ? {} : { llmKey: llmKey.trim() }),
        llmModel: model.trim() === '' ? null : model.trim(),
        defaultMonthlyCapUsd: cap.replace(',', '.'),
        version: current.version,
      }),
    onSuccess: (saved) => {
      setError(null)
      setLlmKey('')
      queryClient.setQueryData(['platform-assistant'], saved)
      notify.success('Chave e teto salvos.')
    },
    onError: (failure: unknown) => {
      setError(message(failure))
    },
  })

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        if (!save.isPending) save.mutate()
      }}
    >
      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}
      <Field
        label="Chave da OpenAI do Aeris"
        hint={
          current.hasLlmKey
            ? 'Gravada. Deixe vazio para manter.'
            : 'Sem chave, o assistente não responde.'
        }
      >
        {(field) => (
          <Input
            {...field}
            type="password"
            autoComplete="off"
            value={llmKey}
            placeholder={current.hasLlmKey ? '••••••••' : 'sk-…'}
            onChange={(event) => {
              setLlmKey(event.target.value)
            }}
          />
        )}
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Modelo" hint={`Vazio: ${current.defaultModel}.`}>
          {(field) => (
            <Input
              {...field}
              value={model}
              placeholder={current.defaultModel}
              onChange={(event) => {
                setModel(event.target.value)
              }}
            />
          )}
        </Field>
        <Field
          label="Teto padrão por conta (US$ por mês)"
          hint="Cada conta pode ter o seu, no diálogo da conta."
        >
          {(field) => (
            <Input
              {...field}
              inputMode="decimal"
              value={cap}
              onChange={(event) => {
                setCap(event.target.value)
              }}
            />
          )}
        </Field>
      </div>
      <div>
        <Button type="submit" size="sm" loading={save.isPending}>
          Salvar
        </Button>
      </div>
    </form>
  )
}

export const PlatformAssistant = ({
  onClose,
}: {
  readonly onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const current = useQuery({
    queryKey: ['platform-assistant'],
    queryFn: fetchPlatformAssistant,
  })
  const unlink = useMutation({
    mutationFn: async (version: number | null) =>
      await unlinkPlatformAssistant(version),
    onSuccess: (saved) => {
      queryClient.setQueryData(['platform-assistant'], saved)
      notify.success('O número do Aeris foi desligado.')
    },
  })
  const data = current.data

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title="Assistente do Aeris"
        description="O número de WhatsApp do Aeris One: os clientes de todas as contas perguntam por ele os números da própria loja e recebem o resumo do dia."
      >
        {data === undefined ? null : (
          <div className="flex flex-col gap-4">
            <Section
              title="O número"
              description={
                data.status === 'unlinked'
                  ? 'Escolha uma instância que não seja de loja nenhuma.'
                  : `${data.instanceName ?? data.instanceId ?? ''}${data.linkedPhone === null ? '' : ` — ${data.linkedPhone}`} · ${STATUS_LABELS[data.status]}`
              }
            >
              {data.status === 'unlinked' ? (
                <NumberPicker current={data} />
              ) : (
                <div className="flex flex-col gap-3">
                  {data.status === 'pairing' ? <Pairing /> : null}
                  <div>
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={unlink.isPending}
                      onClick={() => {
                        unlink.mutate(data.version)
                      }}
                    >
                      Desligar este número
                    </Button>
                  </div>
                </div>
              )}
            </Section>
            <Section title="O GPT">
              <KeyForm key={data.version ?? 'nova'} current={data} />
            </Section>
          </div>
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
